import React, { useState } from 'react';
import { useAppSelector } from 'hooks';
import { Alert, Label } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';
import { formatDateString } from '~libs/dayjs';
import { useToast } from '~libs/toast';

import { findLinkedPackageSchedule } from '~clients/hooks/useActivationChecklist';

import { Button, ErrorResponse, SectionCard, Spinner } from '~components';
import FormSelect from '~components/form-fields/inputs/Select';
import LookupStatusBadge from '~components/LookupStatusBadge';

import { CarePlan } from '~clients/models';

import { CARE_PLAN_LINKED_BADGE } from '~clients/config/carePlanEnums';

import { useUpdateCarePlanMutation } from '~clients/actions/carePlans';

import {
  selectClientPackageSchedules,
  selectClientPackageSchedulesLoading,
} from '~main/selectors/clients/clientPackageSchedules';

interface ComponentProps {
  plan: CarePlan;
}

/**
 * Package schedule linkage. The plan stores the package definition id, so the
 * "linked schedule" is the client's active schedule for that package (see
 * findLinkedPackageSchedule). Picking a schedule from the dropdown persists its
 * package onto the plan. Expects the Overview tab to have fetched the client's
 * active schedules into the clientPackageSchedules slice.
 */
const PackageScheduleSection: React.FC<ComponentProps> = ({ plan }) => {
  const schedules = useAppSelector(selectClientPackageSchedules);
  const schedulesLoading = useAppSelector(selectClientPackageSchedulesLoading);
  const { showToast } = useToast();
  const [updateCarePlan, { isLoading: isSaving, error }] = useUpdateCarePlanMutation();
  const [isEditing, setIsEditing] = useState(false);

  const clientSchedules = schedules.filter(({ client_id }) => client_id === plan.client_id);
  const linkedSchedule = findLinkedPackageSchedule(schedules, plan);

  const scheduleRange = ({ start_date, end_date }: { start_date: string; end_date: string }) =>
    `${formatDateString(start_date)} – ${formatDateString(end_date)}`;

  const scheduleOptions = clientSchedules.map((schedule) => ({
    id: schedule.id,
    name: `${schedule.package.name} (${scheduleRange(schedule)})`,
  }));

  const onSelectSchedule = async (value: number | null) => {
    const schedule = clientSchedules.find(({ id }) => id === value);
    if (!schedule) return;
    try {
      await updateCarePlan({ id: plan.id, package_id: schedule.package.id }).unwrap();
      showToast('success', 'Package schedule linked');
      setIsEditing(false);
    } catch {
      // Surfaced through the mutation error below.
    }
  };

  return (
    <SectionCard
      title="Package Schedule"
      titleTag="h4"
      className="care-plan-overview-section"
      dataCy="care-plan-package-schedule"
    >
      {error !== undefined && (
        <ErrorResponse dataCy="care-plan-package-schedule-error" message={getErrorMessage(error)} />
      )}
      {!isEditing && linkedSchedule && (
        // pe-0 drops the body's right padding so the ms-auto button reaches the card
        // edge, lining up with the Plan Details Save button while staying inline.
        <div
          className="care-plan-overview-section-body care-plan-schedule-row"
          data-cy="care-plan-package-schedule-linked"
        >
          <span className="fw-bold">{linkedSchedule.package.name}</span>
          <span className="text-muted">{scheduleRange(linkedSchedule)}</span>
          <LookupStatusBadge
            status={CARE_PLAN_LINKED_BADGE}
            dataCy="care-plan-package-schedule-badge"
            className="care-plan-linked-badge"
          />
          <Button size="sm" color="warning" className="ms-auto" onClick={() => setIsEditing(true)}>
            Change schedule
          </Button>
        </div>
      )}
      {/* Guard the "unlinked" warning behind the schedules load — the slice is shared and
          starts empty, so an unguarded !linkedSchedule would flash a false warning first. */}
      {!isEditing && !linkedSchedule && schedulesLoading && (
        <div className="care-plan-overview-section-body care-plan-section-loading">
          <Spinner loading />
        </div>
      )}
      {!isEditing && !linkedSchedule && !schedulesLoading && (
        <div className="care-plan-overview-section-body">
          <Alert color="warning" className="care-plan-schedule-alert" data-cy="care-plan-package-schedule-unlinked">
            <span>No active package schedule is linked to this plan.</span>
            <Button size="sm" color="success" className="ms-auto" onClick={() => setIsEditing(true)}>
              Link schedule
            </Button>
          </Alert>
        </div>
      )}
      {isEditing && (
        <div className="care-plan-overview-section-body care-plan-schedule-row">
          {/* Visually hidden — the selector is self-explanatory here, but keeps an accessible name. */}
          <Label for="care-plan-package-schedule-select" className="visually-hidden">
            Package schedule
          </Label>
          <div className="flex-grow-1">
            <FormSelect
              type="select"
              id="care-plan-package-schedule-select"
              name="care-plan-package-schedule-select"
              value={linkedSchedule?.id ?? null}
              options={scheduleOptions}
              placeholder="-- Select a package schedule --"
              disabled={isSaving}
              clearable={false}
              onChange={(_id: string, value: number | null) => onSelectSchedule(value)}
            />
          </div>
          <Button size="sm" color="warning" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
        </div>
      )}
    </SectionCard>
  );
};

export default PackageScheduleSection;
